import React from 'react';
import Button from '../Button';
import Col from '../Col';
import File from '../File';
import { Heading } from '../Form';
import Input from '../Input';
import Row from '../Row';
import Textarea from '../Textarea';
import Loader from '../Loader';
import TextEditor from '../Editor/Editor';
import { onCheckHeicDocumentAndMp4 } from 'src/helpers/CommonHelpers';
import { FaqFormData } from 'src/components/Faqs/CustomHooks/useFaqForm';
import { useFaqFormLogic, UseFaqFormLogicReturn } from './useFaqFormLogic';
import 'react-toastify/dist/ReactToastify.css';
import './FaqForm.scss';

interface IFaqFormProps {
  formData: FaqFormData;
  setFormData: (data: Partial<FaqFormData>) => void;
  isEditing: boolean;
  onClose: () => void;
  onSaveFaq: (faq: FaqFormData, action: 'SaveAndClose' | 'SaveAndAdd') => void;
  isSaving?: boolean;
}

const FaqForm = ({
  formData,
  setFormData,
  isEditing,
  onClose,
  onSaveFaq,
  isSaving = false,
}: IFaqFormProps) => {
  const {
    editorState,
    onEditorChange,
    onSelectDocument,
    handleSaveFaq,
    isFormValid,
  }: UseFaqFormLogicReturn = useFaqFormLogic({
    formData,
    setFormData,
    onSaveFaq,
  });

  return (
    <>
      <Row chonky>
        <Col chonky xs="12" md="6">
          <Heading>FAQ Details</Heading>
          <Input
            value={formData.question || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ question: e.target.value })
            }
            label="Question"
            placeholder="eg., What does this error code mean?"
            required
            block
          />
          <div className="input">
            <div className="input__require">
              <label className="input__label">Answer</label>
              <div className="input__required" />
            </div>
            <div
              style={{
                borderBottom: '0.1rem solid rgba(83, 83, 83, 0.25)',
              }}
              className="input__control"
            >
              <TextEditor
                editorState={editorState}
                onEditorStateChange={onEditorChange}
                readOnly={undefined}
                placeholder={undefined}
              />
            </div>
          </div>
          <br />
          <Input
            value={formData.issue || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ issue: e.target.value })
            }
            label="Category"
            placeholder="eg., Power Issues"
            block
          />
        </Col>
        <Col xs="12" md="6" chonky>
          <Heading>Attachments</Heading>
          <File
            label="File"
            type={['image', 'video', 'pdf']}
            onSelect={(file: any) =>
              onCheckHeicDocumentAndMp4(file) && onSelectDocument(file)
            }
            files={
              formData.uploadType === 'Doc'
                ? []
                : formData.image && formData.image.length === 1
                  ? formData.image
                  : []
            }
            image={
              formData.uploadType === 'Doc'
                ? null
                : formData.image && formData.image.length > 1
                  ? formData.image
                  : null
            }
            required={true}
          />
          {formData.uploadType === 'Doc' && formData.image ? (
            <a href={formData.image} target="_blank" rel="noreferrer">
              <label style={{ paddingBottom: 5 }}>
                {formData.image.split('.com/')[1]}
              </label>
            </a>
          ) : null}
          <div
            style={{
              textAlign: 'center',
              color: '#43467F',
              fontWeight: 700,
            }}
          >
            {' '}
            (OR)
          </div>
          <Textarea
            value={formData.Embed || ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFormData({ Embed: e.target.value })
            }
            label="Embed "
            block
            type="media"
          />
          <Input
            value={formData.linkTitle || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ linkTitle: e.target.value })
            }
            label="Link Title"
            placeholder="eg., How to fix error codes"
            block
          />
          <Textarea
            value={formData.caption || ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFormData({ caption: e.target.value })
            }
            label="Caption"
            placeholder="Provide details."
            block
          />
        </Col>
      </Row>
      {/* Action Row. */}
      <div className="faq-form-action-row">
        {isSaving && <Loader center={true} />}
        {!isEditing && (
          <Button
            color="semantic"
            size="small"
            label="Save and Add Another"
            onClick={() => handleSaveFaq('SaveAndAdd')}
            disabled={!isFormValid || isSaving}
          />
        )}
        <Button
          color="teal"
          size="small"
          label="Save FAQ"
          onClick={() => handleSaveFaq('SaveAndClose')}
          disabled={!isFormValid || isSaving}
        />
      </div>
    </>
  );
};

export default FaqForm;
